import { apiGet } from '@/utils/api';

// Types for Analytics
export interface OverviewAnalytics {
  totalInstitutions: number;
  activeInstitutions: number;
  totalUsers: number;
  activeUsersLast30Days: number;
  newInstitutionsThisMonth: number;
  platformHealth: {
    uptime: number;
    errorRate: number;
    avgResponseTime: number;
  };
}

export interface UserAnalytics {
  totalUsers: number;
  usersByRole: { role: string; count: number }[];
  newUsersThisMonth: number;
  newUsersLastMonth: number;
  activeUsersLast24h: number;
  activeUsersLast7d: number;
  activeUsersLast30d: number;
  topInstitutionsByUsers: { institutionId: string; institutionName: string; userCount: number }[];
  userActivationRate: number;
  inactiveUsers: number;
  inactiveRate: number;
  dau: number;
  mau: number;
  dauMauRatio: number;
  retention?: {
    d7: number;
  };
}

export interface BillingAnalytics {
  // Revenue metrics
  mrr: number;
  arr: number;
  arpa: number;
  newMrr: number;
  churnMrr: number;
  expansionMrr: number;
  contractionMrr: number;
  netNewMrr: number;

  // Health metrics
  churnRate: number;
  grossMrrChurnRate: number;
  netMrrChurnRate: number;
  nrr: number;
  trialConversionRate: number;

  // LTV metrics
  ltv: number;
  estimatedCac: number;
  ltvCacRatio: number;
  avgCustomerLifetimeMonths: number;

  // Subscription counts
  totalSubscriptions: number;
  activeSubscriptions: number;
  statusBreakdown: Record<string, number>;

  // Revenue tracking
  revenueThisMonth: number;
  revenueLastMonth: number;
  revenueGrowth: number;
  outstandingAmount: number;
  outstandingCount: number;

  // TVA
  tvaCollectedThisMonth: number;

  // Distribution
  planDistribution: { planId: string; planName: string; count: number }[];
}

export interface EmailAnalytics {
  totalCampaigns: number;
  activeCampaigns: number;
  totalEmailsSent: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  unsubscribeRate: number;
  sendsThisMonth: number;
  sendsLastMonth: number;
  bestCampaigns: {
    id: string;
    name: string;
    totalSent: number;
    totalOpened: number;
    openRate: number;
  }[];
  listHealth?: {
    totalSubscribers: number;
    newRecipientsThisMonth: number;
    unsubscribesThisMonth: number;
    listGrowthRate: number;
  };
}

export interface TestAnalytics {
  latestExecution: {
    id: string;
    executionId: string;
    totalTests: number;
    passed: number;
    failed: number;
    skipped: number;
    duration: number;
    coveragePercent: number;
    status: string;
    startedAt: string;
  } | null;
  passRate: number;
  coverageTrend: { date: string; coverage: number }[];
  recentExecutions: {
    id: string;
    executionId: string;
    totalTests: number;
    passed: number;
    failed: number;
    status: string;
    startedAt: string;
  }[];
  flakyTests: {
    id: string;
    testName: string;
    suiteName: string;
    flakyCount: number;
  }[];
}

export interface DashboardAnalytics {
  overview: {
    totalInstitutions: number;
    activeInstitutions: number;
    totalUsers: number;
    activeUsersLast30Days: number;
    newInstitutionsThisMonth: number;
  };
  billing: {
    mrr: number;
    arr: number;
    arpa: number;
    totalSubscriptions: number;
    revenueGrowth: number;
    churnRate: number;
    nrr: number;
    netNewMrr: number;
    ltv: number;
    ltvCacRatio: number;
  };
  email: {
    totalCampaigns: number;
    totalEmailsSent: number;
    openRate: number;
    listGrowthRate?: number;
  };
  tests: {
    passRate: number;
    coveragePercent: number;
    totalTests: number;
  };
}

// Session Analytics
export interface SessionAnalytics {
  totalSessions: number;
  avgSessionDuration: number;
  avgSessionDurationFormatted: string;
  avgPagesPerSession: number;
  sessionsByDevice: { device: string; count: number }[];
  sessionsByBrowser: { browser: string; count: number }[];
}

// Feature Usage
export interface FeatureUsage {
  featureName: string;
  uniqueUsers: number;
  totalUsage: number;
  adoptionRate: number;
}

// Page Views
export interface PageViewData {
  pagePath: string;
  uniqueViews: number;
  totalViews: number;
  avgTimeOnPage: number;
  avgTimeOnPageFormatted: string;
}

// At-Risk Institutions
export interface AtRiskInstitution {
  id: string;
  name: string;
  totalUsers: number;
  activeUsers: number;
  activeRate: number;
  engagementDecline: number;
  riskFactors: string[];
}

// Onboarding Metrics
export interface OnboardingMetrics {
  totalUsers: number;
  completedProfiles: number;
  activatedUsers: number;
  verifiedEmails: number;
  onboardingCompletionRate: number;
  activationRate: number;
  emailVerificationRate: number;
  steps: {
    accountCreated: { count: number; rate: number };
    firstLogin: { count: number; rate: number };
    emailVerified: { count: number; rate: number };
    profileCompleted: { count: number; rate: number };
  };
}

// API Functions
export const getOverviewAnalytics = async (): Promise<OverviewAnalytics> => {
  return apiGet<OverviewAnalytics>('/analytics/overview');
};

export const getUserAnalytics = async (): Promise<UserAnalytics> => {
  return apiGet<UserAnalytics>('/analytics/users');
};

export const getBillingAnalytics = async (): Promise<BillingAnalytics> => {
  return apiGet<BillingAnalytics>('/billing/analytics');
};

export const getEmailAnalytics = async (): Promise<EmailAnalytics> => {
  return apiGet<EmailAnalytics>('/email/analytics');
};

export const getTestAnalytics = async (): Promise<TestAnalytics> => {
  return apiGet<TestAnalytics>('/analytics/tests');
};

export const getDashboardAnalytics = async (): Promise<DashboardAnalytics> => {
  return apiGet<DashboardAnalytics>('/analytics/dashboard');
};

// Session Analytics
export const getSessionAnalytics = async (days = 30): Promise<SessionAnalytics> => {
  return apiGet<SessionAnalytics>(`/analytics/sessions?days=${days}`);
};

// Feature Usage
export const getMostUsedFeatures = async (days = 30, limit = 10): Promise<FeatureUsage[]> => {
  return apiGet<FeatureUsage[]>(`/analytics/features?days=${days}&limit=${limit}`);
};

// Page Views
export const getMostVisitedPages = async (days = 30, limit = 10): Promise<PageViewData[]> => {
  return apiGet<PageViewData[]>(`/analytics/pages?days=${days}&limit=${limit}`);
};

// At-Risk Institutions
export const getAtRiskInstitutions = async (): Promise<AtRiskInstitution[]> => {
  return apiGet<AtRiskInstitution[]>('/analytics/at-risk');
};

// Onboarding Metrics
export const getOnboardingMetrics = async (): Promise<OnboardingMetrics> => {
  return apiGet<OnboardingMetrics>('/analytics/onboarding');
};

// Helper to get auth headers for blob exports
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('nexedu_access_token');
  const tenantId = localStorage.getItem('tenantId');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }

  return headers;
};

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';

// Export Functions - use fetch directly for blob responses
export const exportOverviewToExcel = async (): Promise<Blob> => {
  const response = await fetch(`${API_BASE}/api/analytics/export/overview`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Export failed');
  return response.blob();
};

export const exportUsersToExcel = async (): Promise<Blob> => {
  const response = await fetch(`${API_BASE}/api/analytics/export/users`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Export failed');
  return response.blob();
};

export const exportBillingToExcel = async (): Promise<Blob> => {
  const response = await fetch(`${API_BASE}/api/analytics/export/billing`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Export failed');
  return response.blob();
};

export const exportEmailToExcel = async (): Promise<Blob> => {
  const response = await fetch(`${API_BASE}/api/analytics/export/email`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Export failed');
  return response.blob();
};

export const exportTestsToExcel = async (): Promise<Blob> => {
  const response = await fetch(`${API_BASE}/api/analytics/export/tests`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Export failed');
  return response.blob();
};
